import {
  Box,
  Link,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";

import { KeyboardArrowLeft } from "@mui/icons-material";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import React, { useState } from "react";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "../components/CompanyListFeedGrid";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import type { PictureResponse } from "@/types/acquirebook";
import type { HandOffMessage } from "@/types/handOffMessage";
import type {
  FetchParams,
  CommercialPictureRequestPayloadItem,
} from "@/features/commercialBook/types";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";

export interface CheckedCompanyDetail {
  closed: boolean;
  location: string;
  companyName: string;
  corporateNumber: string;
}

export const CompanyList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const SectionWrapper = isMobile ? Box : SectionPaper;
  const compactSx = isMobile
    ? {
        "& .MuiTypography-h4": { fontSize: "1.4rem" },
        "& .MuiTypography-h5": { fontSize: "1.25rem" },
        "& .MuiTypography-h6": { fontSize: "1.1rem" },
        "& .MuiTypography-subtitle1": { fontSize: "1rem" },
        "& .MuiTypography-body1": { fontSize: "0.95rem" },
        "& .MuiTypography-body2": { fontSize: "0.9rem" },
        "& .MuiTypography-caption": { fontSize: "0.78rem" },
        "& .MuiButton-root": { fontSize: "0.9rem", py: 0.4 },
        "& .MuiChip-root": { height: 24, fontSize: "0.8rem" },
        "& .MuiInputBase-input": { fontSize: "0.95rem" },
        "& .MuiInputLabel-root": { fontSize: "0.95rem" },
        "& .MuiFormControlLabel-label": { fontSize: "0.9rem" },
      }
    : undefined;
  const [checkedCompanyDetails, setCheckedCompanyDetails] = useState<
    CheckedCompanyDetail[]
  >([]);
  const currentLocation = useLocation();
  const fetchParams = currentLocation.state as FetchParams | null;
  // 確認画面のモーダルを表示するかしないかを決めるフラグ
  const [openModalFlag, setOpenModalFlag] = useState(false);

  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const isReadOnly = useReadOnlyUser();

  const closeModal = (): void => {
    setOpenModalFlag(false);
  };

  const handleSubmit = (cancelLoading: () => void): void => {
    const reqList: CommercialPictureRequestPayloadItem[] =
      checkedCompanyDetails.map((checkedCompanyDetail) => ({
        matchMethod: "会社法人等番号",
        matchType: "完全一致",
        closed: checkedCompanyDetail.closed,
        kubun: fetchParams?.kubun,
        prefectureName: fetchParams?.prefectureName,
        conditionLocation: fetchParams?.location, // 検索条件の所在
        location: checkedCompanyDetail.location, // 法人一覧で取得した所在
        conditionCompanyName: fetchParams?.companyName,
        companyName: checkedCompanyDetail.companyName,
        corporateNumber: checkedCompanyDetail.corporateNumber,
        // 解析は無料のため取得時は常に実施する（ユーザーによる選択はさせない）
        withCommercialPictureAnalyze: true,
      }));
    apiClient
      .post<PictureResponse>("/commercial-picture-request/", reqList)
      .then((result) => {
        if (result.status === 200) {
          const handOffMessage: HandOffMessage = {
            command: "COMMERCIAL_INFO_SHOW_ACCEPT_MESSAGE",
          };
          navigate("/commercialbook/list", {
            replace: true,
            state: { handOffMessage },
          });
        }
      })
      .catch((e) => {
        console.error(e);
        cancelLoading();
        toast.error("商業登記の取得処理に失敗しました");
      });
  };
  return (
    <PagePaper>
      <Box component="form" sx={compactSx}>
        <Box sx={{ display: "flex" }}>
          {/* ページタイトル */}
          <Box sx={{ display: "flex", mb: 3 }}>
            <ImportContactsIcon sx={{ mr: 1 }} fontSize="large" />
            <PageTitle>会社・法人一覧</PageTitle>
          </Box>
        </Box>
        {fetchParams ? (
          <SectionWrapper>
            <FeedGrid
              checkedCompanyDetails={checkedCompanyDetails}
              setCheckedCompanyDetails={setCheckedCompanyDetails}
              fetchParams={fetchParams}
            />
            {/* 取得ボタン */}
            <Box
              sx={{
                mr: isMobile ? 0 : 2,
                my: 3,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <ReadOnlyTooltip isReadOnly={isReadOnly}>
                <Button
                  label="取得する"
                  onClick={() => {
                    setOpenModalFlag(true);
                  }}
                  variant={ButtonVariantOption.Contained}
                  size={SizingWrapperStyle.SMALL}
                  disabled={isReadOnly || checkedCompanyDetails.length === 0}
                />
              </ReadOnlyTooltip>
            </Box>
          </SectionWrapper>
        ) : (
          // 検索条件入力画面をスキップした場合など
          <Typography>
            申し訳ございません。商業登記簿の取得画面にお戻りいただき再度検索をお試しください。
          </Typography>
        )}

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link href="/commercialbook" sx={{ display: "inline-block" }}>
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>商業登記簿の取得画面に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </Box>
      {/* 確認のモーダル画面 */}
      <Dialog
        open={openModalFlag}
        onClose={(_event, reason) => {
          // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          closeModal();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"確認画面"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            【課金が発生します】商業登記を取得します。
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            取得した商業登記は自動で解析されます（無料）
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant={ButtonVariantOption.Outlined}
            onClick={closeModal}
            label={"キャンセル"}
          />
          <ReadOnlyTooltip isReadOnly={isReadOnly}>
            <SubmitButton
              variant={ButtonVariantOption.Contained}
              onClick={(_event, cancelLoading) => {
                handleSubmit(cancelLoading);
              }}
              label={"取得する"}
              timeout={30000}
              disabled={isReadOnly}
            />
          </ReadOnlyTooltip>
        </DialogActions>
      </Dialog>
    </PagePaper>
  );
};
